/**
 * Customer Portal (E12) — API client.
 *
 * Uses its OWN axios instance and its OWN localStorage keys rather than the
 * shared `services/api.js`. The two must not share token storage: a customer and
 * an ERP user can sign in from the same browser (a merchandiser demoing the
 * portal, a shared machine at a buying office), and one `token` key means the
 * second sign-in silently evicts the first. Separate keys let both sessions
 * coexist, which is also what makes `/portal` and the ERP usable in two tabs.
 */

import axios from 'axios';

import api from './api';

export const PORTAL_TOKEN_KEY = 'portalToken';
export const PORTAL_REFRESH_KEY = 'portalRefreshToken';
export const PORTAL_PROFILE_KEY = 'portalProfile';

const API_ROOT = '/api/customer-portal';

// Mirrors the reserved-subdomain list in apps/tenants/middleware.py and the
// resolution in services/api.js — the portal is served per tenant subdomain, so
// the header has to be derived the same way or a request lands on the master DB.
const RESERVED_SUBDOMAINS = ['www', 'localhost', '127', 'biztimize', 'test', 'dev'];
const FALLBACK_TENANT_ID = process.env.REACT_APP_TENANT_ID || null;

const getTenantId = () => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  if (parts.length < 2 || /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname)) {
    return FALLBACK_TENANT_ID;
  }
  const candidate = parts[0].toLowerCase();
  if (RESERVED_SUBDOMAINS.includes(candidate)) return FALLBACK_TENANT_ID;
  if (parts.length === 2 && parts[1].toLowerCase() !== 'localhost') return null;
  return parts[0];
};

const portalApi = axios.create({
  baseURL: '',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

portalApi.interceptors.request.use((config) => {
  const tenantId = getTenantId();
  if (tenantId) config.headers['X-Tenant-ID'] = tenantId;

  const token = localStorage.getItem(PORTAL_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let isRefreshing = false;
let queued = [];

portalApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    const isAuthFailure = status === 401 && !original?._retry
      && !(original?.url || '').includes('/auth/');

    if (!isAuthFailure) return Promise.reject(error);

    original._retry = true;
    const refresh = localStorage.getItem(PORTAL_REFRESH_KEY);
    if (!refresh) {
      clearPortalSession();
      return Promise.reject(error);
    }

    // Single-flight refresh: several panels load at once on the dashboard, and
    // without this every one of them would post its own refresh and rotate the
    // token out from under the others.
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queued.push({ resolve, reject, original });
      });
    }

    isRefreshing = true;
    try {
      const { data } = await axios.post('/auth/token/refresh/', { refresh }, {
        headers: {
          'Content-Type': 'application/json',
          ...(getTenantId() ? { 'X-Tenant-ID': getTenantId() } : {}),
        },
      });
      localStorage.setItem(PORTAL_TOKEN_KEY, data.access);
      if (data.refresh) localStorage.setItem(PORTAL_REFRESH_KEY, data.refresh);

      isRefreshing = false;
      queued.forEach(({ resolve, original: req }) => {
        req.headers.Authorization = `Bearer ${data.access}`;
        resolve(portalApi(req));
      });
      queued = [];

      original.headers.Authorization = `Bearer ${data.access}`;
      return portalApi(original);
    } catch (refreshError) {
      isRefreshing = false;
      queued.forEach(({ reject }) => reject(refreshError));
      queued = [];
      clearPortalSession();
      return Promise.reject(refreshError);
    }
  },
);

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------
export const storePortalSession = ({ tokens, profile }) => {
  if (tokens?.access) localStorage.setItem(PORTAL_TOKEN_KEY, tokens.access);
  if (tokens?.refresh) localStorage.setItem(PORTAL_REFRESH_KEY, tokens.refresh);
  if (profile) localStorage.setItem(PORTAL_PROFILE_KEY, JSON.stringify(profile));
};

export const clearPortalSession = () => {
  localStorage.removeItem(PORTAL_TOKEN_KEY);
  localStorage.removeItem(PORTAL_REFRESH_KEY);
  localStorage.removeItem(PORTAL_PROFILE_KEY);
};

export const getStoredProfile = () => {
  try {
    const raw = localStorage.getItem(PORTAL_PROFILE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const hasPortalSession = () => Boolean(localStorage.getItem(PORTAL_TOKEN_KEY));

/** Pull the message the API meant the customer to read, not axios's stringified error. */
export const portalErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  const firstField = Object.values(data)[0];
  if (Array.isArray(firstField) && firstField.length) return String(firstField[0]);
  if (typeof firstField === 'string') return firstField;
  return fallback;
};

// ---------------------------------------------------------------------------
// Customer-facing endpoints
// ---------------------------------------------------------------------------
const customerPortalService = {
  login: (email, password) => portalApi.post(`${API_ROOT}/auth/login/`, { email, password }),
  activate: (payload) => portalApi.post(`${API_ROOT}/auth/activate/`, payload),
  forgotPassword: (email) => portalApi.post(`${API_ROOT}/auth/forgot-password/`, { email }),
  logout: () => portalApi.post(`${API_ROOT}/auth/logout/`),
  changePassword: (payload) => portalApi.post(`${API_ROOT}/auth/change-password/`, payload),

  me: () => portalApi.get(`${API_ROOT}/me/`),
  dashboard: () => portalApi.get(`${API_ROOT}/dashboard/`),
  analytics: (params) => portalApi.get(`${API_ROOT}/analytics/`, { params }),

  orders: (params) => portalApi.get(`${API_ROOT}/orders/`, { params }),
  order: (id) => portalApi.get(`${API_ROOT}/orders/${id}/`),
  orderTracking: (id) => portalApi.get(`${API_ROOT}/orders/${id}/tracking/`),

  shipments: (params) => portalApi.get(`${API_ROOT}/shipments/`, { params }),
  shipment: (id) => portalApi.get(`${API_ROOT}/shipments/${id}/`),

  finance: () => portalApi.get(`${API_ROOT}/finance/`),
  statement: (params) => portalApi.get(`${API_ROOT}/finance/statement/`, { params }),

  documents: (params) => portalApi.get(`${API_ROOT}/documents/`, { params }),
  documentDownloadUrl: (row) =>
    `${API_ROOT}/documents/${row.source}/${row.kind}/${row.object_id}/download/`,
  /**
   * Documents are fetched as a blob rather than opened in a new tab: the
   * download endpoint is JWT-authenticated, and a plain window.open sends no
   * Authorization header, so every download would 401.
   */
  downloadDocument: (row) =>
    portalApi.get(customerPortalService.documentDownloadUrl(row), { responseType: 'blob' }),

  tickets: (params) => portalApi.get(`${API_ROOT}/tickets/`, { params }),
  ticket: (id) => portalApi.get(`${API_ROOT}/tickets/${id}/`),
  createTicket: (payload) => portalApi.post(`${API_ROOT}/tickets/`, payload),
  replyToTicket: (id, message) => portalApi.post(`${API_ROOT}/tickets/${id}/reply/`, { message }),

  notifications: (params) => portalApi.get(`${API_ROOT}/notifications/`, { params }),
  markNotificationRead: (id) => portalApi.post(`${API_ROOT}/notifications/${id}/mark-read/`),
  markAllNotificationsRead: () => portalApi.post(`${API_ROOT}/notifications/mark-all-read/`),
};

/** Trigger a browser save for a blob response. */
export const saveBlob = (response, fallbackName = 'document.pdf') => {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = match ? match[1] : fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Internal (ERP staff) endpoints — these ride the ERP session, so they use the
// shared api client, not portalApi.
//
// The list endpoints return a bounded envelope `{results, count, returned,
// truncated, limit}`: the admin screen binds Syncfusion grids, which page,
// sort, filter and search client-side over whatever they are handed, so the
// server caps the fetch (the project has no DEFAULT_PAGINATION_CLASS — a plain
// list would return every audit row ever written). `truncated` is why the cap
// is visible in the UI rather than silent.
// ---------------------------------------------------------------------------
export const customerPortalAdminService = {
  users: (params) => api.get(`${API_ROOT}/admin/portal-users/`, { params }),
  // Customer + company pickers for the invite dialog. Served by the portal's own
  // API, not /crm/api/customers/ — that needs `crm.view_lead`, which a Customer
  // Portal Administrator has no reason to hold, and without it the invite dialog
  // 403s and the feature is unusable.
  inviteOptions: (params) => api.get(`${API_ROOT}/admin/portal-users/invite-options/`, { params }),
  // The named individuals on file for one customer. A portal login is one of
  // the customer's CRM contacts, so the invite dialog picks a person rather than
  // asking the operator to retype one.
  customerContacts: (customerId) => api.get(
    `${API_ROOT}/admin/portal-users/customer-contacts/`, { params: { customer: customerId } },
  ),
  invite: (payload) => api.post(`${API_ROOT}/admin/portal-users/invite/`, payload),
  // Several of one customer's contacts in a single run. Reported per row: one
  // contact with no email on file is not a reason to withhold access from the
  // colleagues picked beside them.
  bulkInvite: (payload) => api.post(`${API_ROOT}/admin/portal-users/bulk-invite/`, payload),
  updateUser: (id, payload) => api.patch(`${API_ROOT}/admin/portal-users/${id}/`, payload),
  resendInvite: (id) => api.post(`${API_ROOT}/admin/portal-users/${id}/resend-invite/`),
  disableUser: (id) => api.post(`${API_ROOT}/admin/portal-users/${id}/disable/`),
  enableUser: (id) => api.post(`${API_ROOT}/admin/portal-users/${id}/enable/`),
  unlockUser: (id) => api.post(`${API_ROOT}/admin/portal-users/${id}/unlock/`),

  configs: (params) => api.get(`${API_ROOT}/admin/config/`, { params }),
  configCompanies: () => api.get(`${API_ROOT}/admin/config/companies/`),
  createConfig: (payload) => api.post(`${API_ROOT}/admin/config/`, payload),
  updateConfig: (id, payload) => api.patch(`${API_ROOT}/admin/config/${id}/`, payload),

  documents: (params) => api.get(`${API_ROOT}/admin/documents/`, { params }),
  // Customer / company / order / category pickers for the share dialog. Same
  // reasoning as `inviteOptions` — /crm/api/ needs CRM permissions a Customer
  // Portal Support user has no reason to hold.
  shareOptions: (params) => api.get(`${API_ROOT}/admin/documents/share-options/`, { params }),
  // Share a file the tenant already stores (an existing IntelligentDocument id).
  shareDocument: (payload) => api.post(`${API_ROOT}/admin/documents/`, payload),
  // Upload from the operator's machine and publish in one call. The explicit
  // multipart header is required because the shared axios instance defaults to
  // `application/json`, which would send the file body unparseable; axios fills
  // in the boundary from the FormData itself (the house pattern — see
  // disputeService.uploadAttachment).
  uploadDocument: (formData) => api.post(
    `${API_ROOT}/admin/documents/upload/`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  ),
  updateDocument: (id, payload) => api.patch(`${API_ROOT}/admin/documents/${id}/`, payload),
  removeDocument: (id) => api.delete(`${API_ROOT}/admin/documents/${id}/`),

  activity: (params) => api.get(`${API_ROOT}/admin/activity/`, { params }),
};

// ---------------------------------------------------------------------------
// Project chat
// ---------------------------------------------------------------------------
/**
 * The SAME viewsets the ERP serves on `/proman/api/chat`, mounted under this
 * prefix because a portal token is `user_type='CUSTOMER'` and
 * `auth_api.authentication` confines those to the customer-facing prefixes —
 * the proman path would 401 no matter what the customer was admitted to.
 *
 * Shaped identically to `promanChatService` so the shared `ProjectChatPanel`
 * drives it unchanged. The only differences are the axios instance (portal
 * token, portal refresh) and the prefix.
 */
/**
 * The portal's own axios instance and API prefix, for the shared helpers that
 * take a client — `webPushService`, which must reach `/api/customer-portal/push/`
 * rather than the ERP `/api/push/` a portal token is refused on.
 */
export const portalHttp = portalApi;
export const PORTAL_API_ROOT = API_ROOT;

export const portalChatService = {
  getRoom: (projectId) =>
    portalApi
      .post(`${API_ROOT}/chat/rooms/for-project/${projectId}/`)
      .then((r) => r.data),
  listRooms: (params) =>
    portalApi.get(`${API_ROOT}/chat/rooms/`, { params }).then((r) => r.data),
  getMembers: (roomId) =>
    portalApi.get(`${API_ROOT}/chat/rooms/${roomId}/members/`).then((r) => r.data),
  markRead: (roomId) =>
    portalApi.post(`${API_ROOT}/chat/rooms/${roomId}/mark-read/`).then((r) => r.data),

  listMessages: (roomId, params = {}) =>
    portalApi
      .get(`${API_ROOT}/chat/messages/`, { params: { room: roomId, ...params } })
      .then((r) => r.data),

  sendMessage: (roomId, body, { replyToId, files } = {}) => {
    const hasFiles = Array.isArray(files) && files.length > 0;
    if (!hasFiles) {
      return portalApi
        .post(`${API_ROOT}/chat/messages/`, {
          room: roomId,
          body,
          reply_to_id: replyToId || null,
        })
        .then((r) => r.data);
    }
    const fd = new FormData();
    fd.append('room', String(roomId));
    if (body) fd.append('body', body);
    if (replyToId) fd.append('reply_to_id', String(replyToId));
    files.forEach((f) => fd.append('files', f));
    return portalApi
      .post(`${API_ROOT}/chat/messages/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  editMessage: (messageId, body) =>
    portalApi
      .patch(`${API_ROOT}/chat/messages/${messageId}/`, { body })
      .then((r) => r.data),
  deleteMessage: (messageId) =>
    portalApi.delete(`${API_ROOT}/chat/messages/${messageId}/`),
  reactMessage: (messageId, emoji) =>
    portalApi
      .post(`${API_ROOT}/chat/messages/${messageId}/react/`, { emoji })
      .then((r) => r.data),
};

export default customerPortalService;
